import { useState } from "react";
import { addMonths, format, parseISO } from "date-fns";
import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  CheckCheck,
  MoreVertical,
  PackageCheck,
  Pencil,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  VBadge,
  VButton,
  VDialog,
  VDialogContent,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuTrigger,
  VField,
  VInput,
} from "@/components/ui";
import type { Deployment, Dossier, ReservationRequest } from "@/v2/shared/model";
import { formatDate, formatPeriod, fullName } from "@/v2/shared/format";
import { assetTypeLabels } from "@/v2/shared/statusMeta";
import { DeploymentStatusBadge, DossierStatusBadge } from "@/v2/shared/statusBadges";
import { findAsset, isDossierRunning } from "@/v2/data/db";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import {
  useCompleteDeployment,
  useExtendDossier,
  useMarkDeployed,
  useRemoveDeployment,
  useUpdateDossierPeriod,
} from "@/v2/data/mutations";
import { PlanDeploymentDialog, ReplaceAssetDialog } from "@/v2/aanvragen/dossierDialogs";
import { DialogFooter } from "@/v2/aanvragen/detailActions";

// Dossieroverzicht (CB-12/CB-22): de toegekende periode, status en verlenging.
export function DossierOverview({
  request,
  dossier,
  canManage,
}: {
  request: ReservationRequest;
  dossier: Dossier;
  canManage: boolean;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const [extendOpen, setExtendOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editStart, setEditStart] = useState(dossier.startDate);
  const [editEnd, setEditEnd] = useState(dossier.endDate);
  const [editError, setEditError] = useState("");
  const extend = useExtendDossier();
  const updatePeriod = useUpdateDossierPeriod();

  const isActive = dossier.status !== "CLOSED";
  const running = isDossierRunning(dossier);
  // Nog niet lopend → periode vrij corrigeren. Lopend → enkel verlengen
  // (binnen de regels). Zo overlappen de twee acties niet.
  const canEditPeriod = canManage && isActive && !running;
  const canExtend =
    canManage && running && request.extension === "possible" && dossier.extensionCount < 2;

  // De toegekende periode kan afwijken van wat de aanvrager vroeg; toon dat
  // verschil enkel wanneer het er is (anders niet de datums herhalen).
  const differsFromRequested =
    dossier.startDate !== request.startDate || dossier.endDate !== request.endDate;

  return (
    <Card padding="lg" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Caption tone="muted">Toegekende periode</Caption>
          <div className="flex items-center gap-1">
            <CardTitle className="tabular-nums">
              {formatPeriod(dossier.startDate, dossier.endDate)}
            </CardTitle>
            {canEditPeriod ? (
              <VButton
                size="iconSm"
                variant="ghost"
                aria-label="Periode aanpassen"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setEditStart(dossier.startDate);
                  setEditEnd(dossier.endDate);
                  setEditError("");
                  setEditOpen(true);
                }}
              >
                <Pencil className="size-3.5" aria-hidden />
              </VButton>
            ) : null}
          </div>
          {differsFromRequested ? (
            <span className="flex flex-wrap items-center gap-2 pt-0.5">
              <VBadge color="warning">Aangepast</VBadge>
              <Caption tone="muted">
                Gevraagd: {formatPeriod(request.startDate, request.endDate)}
              </Caption>
            </span>
          ) : null}
        </div>
        <DossierStatusBadge status={dossier.status} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <div className="flex items-center gap-2">
          <VBadge color={dossier.extensionCount > 0 ? "info" : "neutral"}>
            Verlenging {dossier.extensionCount}/2
          </VBadge>
          {request.extension === "fixed" ? (
            <Caption tone="muted">Vaste einddatum</Caption>
          ) : (
            <Caption tone="muted">Verlenging mogelijk</Caption>
          )}
        </div>
        {canExtend ? (
          <VButton size="sm" variant="outline" onClick={() => setExtendOpen(true)}>
            <CalendarClock className="size-4" aria-hidden />
            Verlengen
          </VButton>
        ) : null}
      </div>

      {/* Periode vrij corrigeren zolang het dossier nog niet lopend is. */}
      <VDialog open={editOpen} onOpenChange={setEditOpen}>
        <VDialogContent
          title="Toegekende periode aanpassen"
          description="Corrigeer de toegekende periode. Dit kan zolang het dossier nog niet lopend is. Eens het loopt, verleng je via de verlengingsregels."
          size="sm"
        >
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <VField label="Begindatum" htmlFor="edit-start" required>
                <VInput
                  id="edit-start"
                  type="date"
                  value={editStart}
                  onChange={(e) => {
                    setEditStart(e.target.value);
                    setEditError("");
                  }}
                />
              </VField>
              <VField label="Einddatum" htmlFor="edit-end" required error={editError}>
                <VInput
                  id="edit-end"
                  type="date"
                  value={editEnd}
                  onChange={(e) => {
                    setEditEnd(e.target.value);
                    setEditError("");
                  }}
                />
              </VField>
            </div>
            <DialogFooter
              confirmLabel="Opslaan"
              pending={updatePeriod.isPending}
              onCancel={() => setEditOpen(false)}
              onConfirm={() => {
                if (!editStart || !editEnd || editEnd <= editStart) {
                  setEditError("De einddatum moet na de begindatum liggen.");
                  return;
                }
                updatePeriod.mutate(
                  {
                    requestId: request.id,
                    dossierId: dossier.id,
                    startDate: editStart,
                    endDate: editEnd,
                    author,
                  },
                  { onSuccess: () => setEditOpen(false), onError: (e) => setEditError(e.message) },
                );
              }}
            />
          </div>
        </VDialogContent>
      </VDialog>

      {/* CB-22 — verlenging (standaard één maand, max. twee) */}
      <VDialog open={extendOpen} onOpenChange={setExtendOpen}>
        <VDialogContent
          title="Dossier verlengen"
          description="De einddatum van het dossier schuift één maand op. De verlenging wordt gelogd in de historiek."
          size="sm"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-border px-4 py-3">
              <Body weight="semibold">Periode</Body>
              <div className="mt-1 flex items-center gap-2 tabular-nums">
                <span className="text-muted-foreground">{formatDate(dossier.endDate)}</span>
                <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
                <Body weight="medium">
                  {formatDate(format(addMonths(parseISO(dossier.endDate), 1), "yyyy-MM-dd"))}
                </Body>
              </div>
              <Caption tone="muted" className="mt-1 block">
                Verlenging {dossier.extensionCount + 1} van 2
              </Caption>
            </div>
            <DialogFooter
              confirmLabel="Verlengen"
              pending={extend.isPending}
              onCancel={() => setExtendOpen(false)}
              onConfirm={() =>
                extend.mutate(
                  { requestId: request.id, dossierId: dossier.id, author },
                  { onSuccess: () => setExtendOpen(false) },
                )
              }
            />
          </div>
        </VDialogContent>
      </VDialog>
    </Card>
  );
}

// Inzetten binnen het dossier (CB-16/CB-39): plannen, plaatsing/ophaling
// registreren, vervangen of verwijderen.
export function DossierDeployments({
  request,
  dossier,
  canManage,
}: {
  request: ReservationRequest;
  dossier: Dossier;
  canManage: boolean;
}): React.JSX.Element {
  const [planOpen, setPlanOpen] = useState(false);
  const isActive = dossier.status !== "CLOSED";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <CardTitle className="text-sm">Inzetten</CardTitle>
        {canManage && isActive ? (
          <VButton size="sm" onClick={() => setPlanOpen(true)}>
            <CalendarPlus className="size-4" aria-hidden />
            Inzet plannen
          </VButton>
        ) : null}
      </div>

      {dossier.deployments.length === 0 ? (
        <EmptyState
          title="Nog geen inzet gepland"
          description="Het dossier staat op de wachtrij tot je een inzet met een asset plant."
        />
      ) : (
        <div className="space-y-3">
          {dossier.deployments.map((dep) => (
            <DeploymentRow
              key={dep.id}
              request={request}
              dossier={dossier}
              deployment={dep}
              canManage={canManage && isActive}
            />
          ))}
        </div>
      )}

      <PlanDeploymentDialog
        open={planOpen}
        onOpenChange={setPlanOpen}
        requestId={request.id}
        dossier={dossier}
      />
    </div>
  );
}

function DeploymentRow({
  request,
  dossier,
  deployment,
  canManage,
}: {
  request: ReservationRequest;
  dossier: Dossier;
  deployment: Deployment;
  canManage: boolean;
}): React.JSX.Element {
  const user = useCurrentUser();
  const author = fullName(user.firstName, user.lastName);
  const asset = findAsset(deployment.assetId);

  const [replaceOpen, setReplaceOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const [actualStart, setActualStart] = useState(deployment.expectedStart);
  // Voorvullen met de plaatsingsfirma uit het plannen van de inzet (CB-39):
  // meestal plaatst diezelfde firma ook. Leeg bij een plaatsing door PZA zelf.
  const [executedBy, setExecutedBy] = useState(deployment.externalFirm ?? "");
  const [actualEnd, setActualEnd] = useState(deployment.expectedEnd);
  const [error, setError] = useState("");

  const markDeployed = useMarkDeployed();
  const complete = useCompleteDeployment();
  const remove = useRemoveDeployment();

  const isPlanned = deployment.status === "PLANNED";
  const isDeployed = deployment.status === "DEPLOYED";
  const showMenu = canManage && (isPlanned || isDeployed);

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Body className="font-medium">
              {asset ? `${asset.number} · ${assetTypeLabels[asset.type]}` : "Onbekend asset"}
            </Body>
            <DeploymentStatusBadge status={deployment.status} />
          </div>
          <Caption tone="muted" as="p">
            Verwacht {formatPeriod(deployment.expectedStart, deployment.expectedEnd)} ·{" "}
            {deployment.externalFirm ?? "PZA (zelf geplaatst)"}
          </Caption>
          {deployment.actualStart ? (
            <Caption tone="muted" as="p">
              Effectief geplaatst {formatDate(deployment.actualStart)}
              {deployment.executedBy ? ` door ${deployment.executedBy}` : ""}
              {deployment.actualEnd ? ` · opgehaald ${formatDate(deployment.actualEnd)}` : ""}
            </Caption>
          ) : null}
        </div>

        {showMenu ? (
          <div className="flex shrink-0 items-center gap-1">
            {isPlanned ? (
              <VButton size="sm" variant="outline" onClick={() => setDeployOpen(true)}>
                <PackageCheck className="size-4" aria-hidden />
                Plaatsing registreren
              </VButton>
            ) : null}
            {isDeployed ? (
              <VButton size="sm" variant="outline" onClick={() => setCompleteOpen(true)}>
                <CheckCheck className="size-4" aria-hidden />
                Afronden
              </VButton>
            ) : null}
            <VDropdownMenu>
              <VDropdownMenuTrigger asChild>
                <VButton variant="ghost" size="iconSm" aria-label="Meer acties voor deze inzet">
                  <MoreVertical className="size-4" aria-hidden />
                </VButton>
              </VDropdownMenuTrigger>
              <VDropdownMenuContent align="end" className="min-w-[12rem]">
                <VDropdownMenuItem className="gap-2" onSelect={() => setReplaceOpen(true)}>
                  <RefreshCcw className="size-4" aria-hidden />
                  Asset vervangen
                </VDropdownMenuItem>
                {isPlanned ? (
                  <VDropdownMenuItem className="gap-2" onSelect={() => setRemoveOpen(true)}>
                    <Trash2 className="size-4" aria-hidden />
                    Inzet verwijderen
                  </VDropdownMenuItem>
                ) : null}
              </VDropdownMenuContent>
            </VDropdownMenu>
          </div>
        ) : null}
      </div>

      <ReplaceAssetDialog
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        requestId={request.id}
        dossierId={dossier.id}
        deployment={deployment}
      />

      {/* CB-39 — effectieve plaatsing registreren */}
      <VDialog open={deployOpen} onOpenChange={(o) => (!o ? setDeployOpen(false) : null)}>
        <VDialogContent
          title="Plaatsing registreren"
          description="Registreer de effectieve plaatsingsdatum en wie de plaatsing uitvoerde. De inzet gaat naar “Geplaatst”."
          size="sm"
        >
          <div className="space-y-4">
            <VField label="Effectieve plaatsingsdatum" htmlFor={`dep-actual-${deployment.id}`} required>
              <VInput
                id={`dep-actual-${deployment.id}`}
                type="date"
                value={actualStart}
                onChange={(e) => setActualStart(e.target.value)}
              />
            </VField>
            <VField
              label="Uitgevoerd door"
              htmlFor={`dep-by-${deployment.id}`}
              required
              hint={
                deployment.externalFirm
                  ? "Overgenomen van de plaatsingsfirma. Pas aan indien nodig."
                  : undefined
              }
              error={error}
            >
              <VInput
                id={`dep-by-${deployment.id}`}
                value={executedBy}
                onChange={(e) => {
                  setExecutedBy(e.target.value);
                  setError("");
                }}
              />
            </VField>
            <DialogFooter
              confirmLabel="Registreren"
              pending={markDeployed.isPending}
              onCancel={() => setDeployOpen(false)}
              onConfirm={() => {
                if (!actualStart || !executedBy.trim()) {
                  setError("Datum en uitvoerder zijn verplicht.");
                  return;
                }
                markDeployed.mutate(
                  {
                    requestId: request.id,
                    dossierId: dossier.id,
                    deploymentId: deployment.id,
                    actualStart,
                    executedBy,
                    author,
                  },
                  { onSuccess: () => setDeployOpen(false) },
                );
              }}
            />
          </div>
        </VDialogContent>
      </VDialog>

      {/* CB-39 — ophaling registreren, inzet afronden */}
      <VDialog open={completeOpen} onOpenChange={(o) => (!o ? setCompleteOpen(false) : null)}>
        <VDialogContent
          title="Inzet afronden"
          description="Registreer de effectieve einddatum bij ophaling. De inzet gaat naar “Afgerond”."
          size="sm"
        >
          <div className="space-y-4">
            <VField label="Effectieve einddatum" htmlFor={`dep-end-${deployment.id}`} required>
              <VInput
                id={`dep-end-${deployment.id}`}
                type="date"
                value={actualEnd}
                onChange={(e) => setActualEnd(e.target.value)}
              />
            </VField>
            <DialogFooter
              confirmLabel="Afronden"
              pending={complete.isPending}
              onCancel={() => setCompleteOpen(false)}
              onConfirm={() =>
                complete.mutate(
                  {
                    requestId: request.id,
                    dossierId: dossier.id,
                    deploymentId: deployment.id,
                    actualEnd,
                    author,
                  },
                  { onSuccess: () => setCompleteOpen(false) },
                )
              }
            />
          </div>
        </VDialogContent>
      </VDialog>

      {/* CB-16 — geplande inzet verwijderen */}
      <VDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <VDialogContent
          title="Inzet verwijderen"
          description={`De geplande inzet van ${asset?.number ?? "dit asset"} wordt verwijderd. Het asset komt weer vrij voor die periode. Valt de laatste inzet weg, dan gaat het dossier terug op de wachtrij.`}
          size="sm"
        >
          <DialogFooter
            confirmLabel="Verwijderen"
            destructive
            pending={remove.isPending}
            onCancel={() => setRemoveOpen(false)}
            onConfirm={() =>
              remove.mutate(
                {
                  requestId: request.id,
                  dossierId: dossier.id,
                  deploymentId: deployment.id,
                  author,
                },
                { onSuccess: () => setRemoveOpen(false) },
              )
            }
          />
        </VDialogContent>
      </VDialog>
    </div>
  );
}
